import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './QuotaDisplay.css';

function QuotaDisplay({ compact = false, showUpgrade = true }) {
  const [usageData, setUsageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUsageData();
  }, []);

  const fetchUsageData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[DEBUG] QuotaDisplay: No auth token found');
        }
        setIsLoading(false);
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('[DEBUG] QuotaDisplay: Fetching usage data...');
      }
      const response = await fetch('/api/usage/current', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (process.env.NODE_ENV === 'development') {
          console.log('[DEBUG] QuotaDisplay: Usage data received:', data);
        }
        setUsageData(data);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error('[DEBUG] QuotaDisplay: Failed to load usage data:', response.status, response.statusText);
        }
        setError('Failed to load usage data');
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[DEBUG] QuotaDisplay: Network request failed:', err);
      }
    
      if (err.name === 'AbortError') {
        setError('Request was aborted');
      } else if (err.message.includes('Failed to fetch')) {
        setError('Unable to reach the server. Please check your connection.');
      } else {
        setError(`Network error: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getProgressBarColor = (warningLevel) => {
    switch (warningLevel) {
      case 'critical': return '#dc3545'; // red
      case 'warning': return '#ffc107'; // yellow
      default: return '#28a745'; // green
    }
  };

  const formatMinutes = (minutes) => {
    if (minutes === 'unlimited') return 'Unlimited';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  if (isLoading) {
    return compact ? 
      <div className="quota-display compact loading">Loading...</div> :
      <div className="quota-display loading">Loading usage data...</div>;
  }

  if (error || !usageData) {
    return compact ? null : 
      <div className="quota-display error">Unable to load usage data</div>;
  }

  const {
    tier,
    transcriptionCount,
    transcriptionMinutes,
    remainingTranscriptions,
    remainingMinutes,
    limitReached,
    limits
  } = usageData;

  // Calculate warning level based on usage
  const getWarningLevel = () => {
    if (limitReached) return 'critical';

    if (limits.monthlyTranscriptions > 0) {
      const percentage = (transcriptionCount / limits.monthlyTranscriptions) * 100;
      if (percentage >= 90) return 'critical';
      if (percentage >= 75) return 'warning';
    }

    return 'normal';
  };

  const warningLevel = getWarningLevel();
  const isUnlimited = limits.monthlyTranscriptions === -1 && limits.monthlyMinutes === -1;

  if (compact) {
    return (
      <div className={`quota-display compact ${warningLevel}`}>
        <div className="quota-summary">
          <span className="plan-name">{limits.planName}</span>
          {limits.monthlyTranscriptions > 0 && (
            <span className="usage-text">
              {transcriptionCount} / {limits.monthlyTranscriptions} transcriptions
            </span>
          )}
          {isUnlimited && (
            <span className="unlimited-badge">∞</span>
          )}
        </div>
        {limits.monthlyTranscriptions > 0 && (
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${Math.min((transcriptionCount / limits.monthlyTranscriptions) * 100, 100)}%`,
                backgroundColor: getProgressBarColor(warningLevel)
              }}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`quota-display ${warningLevel}`}>
      <div className="quota-header">
        <h3>Current Plan: {limits.planName}</h3>
        {tier === 'free' && showUpgrade && (
          <Link to="/plans-and-payment" className="upgrade-link">
            Upgrade
          </Link>
        )}
      </div>

      {limits.monthlyTranscriptions > 0 ? (
        <div className="quota-content">
          <div className="usage-stats">
            <div className="stat">
              <span className="label">Transcriptions used:</span>
              <span className="value">{transcriptionCount} / {limits.monthlyTranscriptions}</span>
            </div>
            <div className="stat">
              <span className="label">Remaining:</span>
              <span className="value">{Math.max(0, limits.monthlyTranscriptions - transcriptionCount)}</span>
            </div>
          </div>

          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${Math.min((transcriptionCount / limits.monthlyTranscriptions) * 100, 100)}%`,
                backgroundColor: getProgressBarColor(warningLevel)
              }}
            />
          </div>

          {limitReached ? (
            <div className="quota-message critical">
              <p>You've reached your monthly limit of {limits.monthlyTranscriptions} transcriptions.</p>
              {showUpgrade && tier === 'free' && (
                <Link to="/plans-and-payment" className="upgrade-btn">
                  Upgrade to Basic Plan - $2/month
                </Link>
              )}
              {showUpgrade && tier === 'basic' && (
                <Link to="/plans-and-payment" className="upgrade-btn">
                  Upgrade to Premium Plan - $8/month
                </Link>
              )}
            </div>
          ) : warningLevel === 'warning' ? (
            <div className="quota-message warning">
              <p>You're approaching your monthly limit. {Math.max(0, limits.monthlyTranscriptions - transcriptionCount)} transcriptions remaining.</p>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="quota-content">
          <div className="usage-stats">
            <div className="stat">
              <span className="label">Transcriptions:</span>
              <span className="value unlimited">Unlimited ∞</span>
            </div>
            <div className="stat">
              <span className="label">This month:</span>
              <span className="value">{transcriptionCount} transcriptions</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QuotaDisplay;